import React from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Dialog,
  Typography,
  DialogContent,
  IconButton,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { getStatePaymentTranslationKey } from '../../../utils/merchants';
import { formatCurrencyValue } from '../../../utils/formatter';

import DialogDetailsPaymentItem from './Item';
import styles from './styles';

const useStyles = makeStyles(styles);

const DialogDetailsPayment = ({ open, onClose, data }) => {
  const { t } = useTranslation('common');
  const classes = useStyles();

  const {
    created_at: createdAt,
    crypto_currency: cryptoCurrency,
    fiat,
    id,
    movement_amount: movementAmount,
    payer,
    quotation,
    source_account_identifier: sourceAccountIdentifier,
    summary_state: summaryState,
    token,
    total_amount: totalAmount,
    tributary_identifier_payer: tributaryIdentifierPayer,
    coelsa_id: coelsaId,
    collector_account: collectorAccount,
  } = data;

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      aria-labelledby="dialog-details-payment"
      open={open}
      keepMounted
      maxWidth={false}
      classes={{
        container: classes.dialogContainer,
        paperWidthFalse: classes.dialogPaper,
      }}
      scroll="body"
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        className={classes.dialogCloseButton}
      >
        <CloseIcon color="secondary" />
      </IconButton>

      <Typography component="h2" className={classes.dialogTitle}>
        {t('payments.paymentDetails')}
      </Typography>

      <DialogContent className={classes.dialogContent}>
        <DialogDetailsPaymentItem
          label={t('payments.date')}
          value={moment(createdAt).format('DD/MM/YYYY HH:mm')}
        />

        <DialogDetailsPaymentItem
          label={t('payments.id')}
          value={id.toString()}
        />

        <DialogDetailsPaymentItem
          label={t('payments.amountCharged')}
          value={`${formatCurrencyValue(totalAmount, {
            style: 'decimal',
          })} ${fiat.toUpperCase()}`}
        />

        <DialogDetailsPaymentItem
          label={t('payments.quotation')}
          value={quotation}
        />

        <DialogDetailsPaymentItem label={t('payments.payedBy')} value={payer} />

        <DialogDetailsPaymentItem
          label={t('payments.accredited')}
          value={
            movementAmount !== null
              ? `${formatCurrencyValue(movementAmount, {
                  style: 'decimal',
                })} ${cryptoCurrency}`
              : ''
          }
        />

        <DialogDetailsPaymentItem
          label={t('payments.state')}
          value={t(getStatePaymentTranslationKey(summaryState))}
        />

        <DialogDetailsPaymentItem
          label={t('payments.identification')}
          value={tributaryIdentifierPayer}
        />

        <DialogDetailsPaymentItem
          label={t('payments.sourceAccountIdentifier')}
          value={sourceAccountIdentifier}
        />

        <DialogDetailsPaymentItem
          label={t('payments.referencialCode')}
          value={token}
        />

        {coelsaId && (
          <DialogDetailsPaymentItem
            label={t('payments.coelsaId')}
            value={coelsaId}
          />
        )}

        {collectorAccount?.cvu_cbu && (
          <DialogDetailsPaymentItem
            label={t('payments.cvuCollector')}
            value={collectorAccount.cvu_cbu}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

DialogDetailsPayment.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    created_at: PropTypes.string,
    crypto_currency: PropTypes.string,
    fiat: PropTypes.string,
    id: PropTypes.number,
    invoice_url: PropTypes.string,
    kind: PropTypes.string,
    movement_amount: PropTypes.string,
    payer: PropTypes.string,
    quotation: PropTypes.string,
    source_account_identifier: PropTypes.string,
    summary_state: PropTypes.string,
    token: PropTypes.string,
    total_amount: PropTypes.number,
    tributary_identifier_payer: PropTypes.string,
    coelsa_id: PropTypes.string,
    collector_account: PropTypes.shape({
      cvu_cbu: PropTypes.string,
      label_alias: PropTypes.string,
    }),
  }),
};

DialogDetailsPayment.defaultProps = {
  data: null,
};

export default DialogDetailsPayment;
